// Staff/coach-facing equestrian show-record card on the athlete detail page.
// Two derived summaries — NCEA win/loss by event and IHSA points + qualifying
// tier by ladder/season — plus the individual result rows with verify seals.
// The summary comes straight from the list endpoint's {results, summary}; verify
// only flips a row's `verified` (see useResultVerify). Staff/partners
// (perms.canVerify) get a toggle seal, coaches see it read-only. Equestrian-only.
import React from 'react';
import SealButton from './SealButton';
import useResultVerify from './useResultVerify';
import {
  FORMAT_LABEL, NCEA_EVENT_LABEL, LADDER_LABEL, RESULT_LABEL, QUALIFIED_FOR_LABEL,
} from '../../lib/equestrian';

function NceaSummary({ ncea }) {
  const events = Object.keys(ncea || {});
  if (events.length === 0) return null;
  return (
    <div className="ae-eq-summary">
      <div className="ae-mark-group__head">NCEA — Win / Loss</div>
      <div className="ae-eq-summary__grid">
        {events.map(ev => (
          <div key={ev} className="ae-eq-summary__cell">
            <span className="ae-eq-summary__label">{NCEA_EVENT_LABEL[ev] || ev}</span>
            <span className="ae-eq-summary__value">{ncea[ev].wins}–{ncea[ev].losses}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IhsaSummary({ ihsa }) {
  const ladders = Object.keys(ihsa || {});
  if (ladders.length === 0) return null;
  return (
    <div className="ae-eq-summary">
      <div className="ae-mark-group__head">IHSA — Points</div>
      <div className="ae-eq-summary__grid">
        {ladders.map(ladder => (
          Object.keys(ihsa[ladder]).sort().map(year => {
            const b = ihsa[ladder][year];
            return (
              <div key={`${ladder}-${year}`} className="ae-eq-summary__cell">
                <span className="ae-eq-summary__label">
                  {LADDER_LABEL[ladder] || ladder} · {year}
                </span>
                <span className="ae-eq-summary__value">
                  {b.points} pts
                  {b.qualified_for && (
                    <span className="ae-mark-badge ae-mark-badge--sb">
                      {QUALIFIED_FOR_LABEL[b.qualified_for] || b.qualified_for}
                    </span>
                  )}
                </span>
              </div>
            );
          })
        ))}
      </div>
    </div>
  );
}

function resultMeta(r) {
  const parts = [];
  if (r.format === 'ncea') {
    if (r.event) parts.push(NCEA_EVENT_LABEL[r.event] || r.event);
    if (r.opponent) parts.push(`vs ${r.opponent}`);
  } else if (r.format === 'ihsa') {
    if (r.ladder) parts.push(LADDER_LABEL[r.ladder] || r.ladder);
    if (r.level) parts.push(r.level);
    if (r.placement != null) parts.push(`Place ${r.placement}`);
    if (r.drawn_horse) parts.push(`Horse: ${r.drawn_horse}`);
  }
  return parts;
}

export default function EquestrianShowRecordCard({ userId, results, setResults, summary, canVerify, apiUrl, getConfig }) {
  const { toggleVerify, busyId } = useResultVerify({ userId, apiUrl, getConfig, setResults });
  if (!results || results.length === 0) return null;

  const sum = summary || { ncea: {}, ihsa: {} };
  // Group result rows by format, NCEA first.
  const order = { ncea: 0, ihsa: 1 };
  const byFormat = new Map();
  for (const r of results) {
    if (!byFormat.has(r.format)) byFormat.set(r.format, []);
    byFormat.get(r.format).push(r);
  }
  const groups = [...byFormat.entries()]
    .sort((a, b) => (order[a[0]] ?? 9) - (order[b[0]] ?? 9))
    .map(([fmt, rows]) => ({ fmt, label: FORMAT_LABEL[fmt] || fmt, rows }));

  return (
    <div className="ae-card ae-card--pad">
      <div className="ae-card__head">
        <h3 className="ae-card__title">Show Record</h3>
        {canVerify && <span className="ae-card__hint">Click a seal to verify</span>}
      </div>

      <NceaSummary ncea={sum.ncea} />
      <IhsaSummary ihsa={sum.ihsa} />

      <div className="ae-marks" style={{ marginTop: 14 }}>
        {groups.map(g => (
          <div key={g.fmt} className="ae-mark-group">
            <div className="ae-mark-group__head">{g.label} Results</div>
            {g.rows.map(r => {
              const meta = resultMeta(r);
              return (
                <div key={r.id} className="ae-accolade">
                  <div className="ae-accolade__body">
                    <div className="ae-accolade__title">
                      {r.meet_name || (r.format === 'ncea' ? 'Dual Meet' : 'Show')}
                      {r.format === 'ncea' && r.result && (
                        <span className={`ae-mark-badge ${r.result === 'win' ? 'ae-mark-badge--win' : 'ae-mark-badge--loss'}`}>
                          {RESULT_LABEL[r.result] || r.result}
                        </span>
                      )}
                    </div>
                    <div className="ae-accolade__meta">
                      {meta.map((m, i) => <span key={i} className="ae-accolade__src">{m}</span>)}
                      {r.meet_date && <span className="ae-accolade__src">{r.meet_date}</span>}
                      {r.season_year && <span className="ae-accolade__src">{r.season_year}</span>}
                    </div>
                  </div>
                  {canVerify ? (
                    <SealButton
                      className="ae-accolade__seal"
                      state={r.verified ? 'verified' : 'unverified'}
                      title={r.verified ? 'Verified — click to unverify' : 'Not verified — click to verify'}
                      disabled={busyId === r.id}
                      onToggle={() => toggleVerify(r)}
                    />
                  ) : (
                    r.verified && (
                      <SealButton className="ae-accolade__seal" state="verified" title="Verified" disabled onToggle={() => {}} />
                    )
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
